import type { LightLayout, Vec2 } from './types.ts';

export interface Pt {
  x: number;
  y: number;
}

/** One LED node in image pixel space. */
export interface LightNode extends Pt {
  /** Index of the node (single) or the pair along its run; drives color patterns. */
  group: number;
  /** 0 for singles; 0/1 for the two LEDs of a pair. */
  member: 0 | 1;
}

export function toPx(p: Vec2, width: number, height: number): Pt {
  return { x: p.x * width, y: p.y * height };
}

export function toPxList(points: Vec2[], width: number, height: number): Pt[] {
  return points.map((p) => toPx(p, width, height));
}

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function polylineLength(pts: Pt[]): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += dist(pts[i - 1], pts[i]);
  return len;
}

/** Cumulative arc length at each vertex. */
function cumulative(pts: Pt[]): number[] {
  const acc = [0];
  for (let i = 1; i < pts.length; i++) acc.push(acc[i - 1] + dist(pts[i - 1], pts[i]));
  return acc;
}

/** Point at arc length `s` along the polyline (clamped to its ends). */
export function pointAtLength(pts: Pt[], acc: number[], s: number): Pt {
  const total = acc[acc.length - 1];
  const t = Math.max(0, Math.min(total, s));
  let i = 1;
  while (i < acc.length - 1 && acc[i] < t) i++;
  const seg = acc[i] - acc[i - 1];
  const u = seg > 1e-9 ? (t - acc[i - 1]) / seg : 0;
  const a = pts[i - 1];
  const b = pts[i];
  return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
}

/**
 * Evenly distributes LED nodes along a polyline. The pitch is adjusted slightly so the
 * run starts and ends half a pitch from its ends, which is how installers space a run.
 * For pairs, each group holds two LEDs `pairGap` apart, centered on the group position.
 */
export function layoutNodes(
  pts: Pt[],
  pitchPx: number,
  layout: LightLayout,
  pairGapPx: number,
): LightNode[] {
  if (pts.length < 2 || pitchPx <= 0) return [];
  const acc = cumulative(pts);
  const total = acc[acc.length - 1];
  if (total < 1) return [];

  const count = Math.max(1, Math.round(total / pitchPx));
  const pitch = total / count;
  // Keep pairs reading as pairs: the gap inside a pair stays well under the gap between pairs.
  const half = Math.min(pairGapPx, pitch * 0.7) / 2;
  const nodes: LightNode[] = [];

  for (let g = 0; g < count; g++) {
    const s = (g + 0.5) * pitch;
    if (layout === 'pair') {
      nodes.push({ ...pointAtLength(pts, acc, s - half), group: g, member: 0 });
      nodes.push({ ...pointAtLength(pts, acc, s + half), group: g, member: 1 });
    } else {
      nodes.push({ ...pointAtLength(pts, acc, s), group: g, member: 0 });
    }
  }
  return nodes;
}

/** Shortest distance from `p` to segment `ab`, plus the projection parameter. */
export function distToSegment(p: Pt, a: Pt, b: Pt): { d: number; t: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return { d: Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t)), t };
}

export function distToPolyline(p: Pt, pts: Pt[]): { d: number; segment: number; t: number } {
  let best = { d: Infinity, segment: -1, t: 0 };
  if (pts.length === 1) return { d: dist(p, pts[0]), segment: 0, t: 0 };
  for (let i = 1; i < pts.length; i++) {
    const r = distToSegment(p, pts[i - 1], pts[i]);
    if (r.d < best.d) best = { d: r.d, segment: i - 1, t: r.t };
  }
  return best;
}

/** Snaps `p` so the segment from `anchor` is horizontal, vertical or 45° when close to one. */
export function snapAngle(anchor: Pt, p: Pt, toleranceDeg = 4): Pt {
  const dx = p.x - anchor.x;
  const dy = p.y - anchor.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return p;
  const angle = Math.atan2(dy, dx);
  const step = Math.PI / 4;
  const snapped = Math.round(angle / step) * step;
  if (Math.abs(angle - snapped) > (toleranceDeg * Math.PI) / 180) return p;
  return { x: anchor.x + Math.cos(snapped) * len, y: anchor.y + Math.sin(snapped) * len };
}

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
